import { auth } from '../config/firebase';

const BACKEND_BASE =
  (import.meta as ImportMeta & { env?: Record<string, string> }).env
    ?.VITE_MORAFAA_BACKEND_URL ??
  process.env.REACT_APP_MORAFAA_BACKEND_URL ??
  'https://morafaa-backend.vercel.app';

export const AD_FREE_MONTHLY_PRODUCT_ID = 'ad_free_monthly';

export interface PaymentEntitlement {
  productId: string;
  kind: string;
  status: string;
  active: boolean;
  permanent: boolean;
  startsAt?: number;
  renewsAt?: number;
  endsAt?: number;
  updatedAt: number;
}

export interface CreateSessionResult {
  orderId: string;
  sessionUrl: string;
  reused: boolean;
}

function newIdempotencyKey(): string {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  let out = '';
  for (let i = 0; i < 32; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

async function bearerToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(false);
}

/** OW-176 — fail-open: returns [] on error so ads stay on. */
export async function fetchEntitlements(): Promise<PaymentEntitlement[]> {
  try {
    const token = await bearerToken();
    if (!token) return [];
    const res = await fetch(`${BACKEND_BASE}/api/payment/entitlements`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { entitlements?: PaymentEntitlement[] };
    return Array.isArray(json.entitlements) ? json.entitlements : [];
  } catch {
    return [];
  }
}

export function isAdFreeActive(entitlements: PaymentEntitlement[]): boolean {
  return entitlements.some(
    (e) => e.productId === AD_FREE_MONTHLY_PRODUCT_ID && e.active,
  );
}

export function adFreeEntitlement(
  entitlements: PaymentEntitlement[],
): PaymentEntitlement | undefined {
  return entitlements.find((e) => e.productId === AD_FREE_MONTHLY_PRODUCT_ID);
}

/** OW-174 — server create-session for ad_free_monthly. */
export async function createAdFreeCheckoutSession(): Promise<CreateSessionResult> {
  const token = await bearerToken();
  if (!token) {
    throw new Error('Sign in required');
  }
  const res = await fetch(`${BACKEND_BASE}/api/payment/create-session`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      productId: AD_FREE_MONTHLY_PRODUCT_ID,
      idempotencyKey: newIdempotencyKey(),
    }),
  });
  const json = (await res.json().catch(() => ({}))) as {
    orderId?: string;
    sessionUrl?: string;
    reused?: boolean;
    error?: string;
  };
  if (!res.ok || !json.sessionUrl || !json.orderId) {
    throw new Error(json.error || `Checkout failed (${res.status})`);
  }
  return {
    orderId: json.orderId,
    sessionUrl: json.sessionUrl,
    reused: Boolean(json.reused),
  };
}
